import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  notificationPreferencesService,
  type CreateUserNotificationPreferenceInput,
  type UpdateUserNotificationPreferenceInput,
} from '../services/notificationPreferencesService';

// ============================================================================
// USER PREFERENCES (for current user)
// ============================================================================

export function useMyNotificationPreferences() {
  return useQuery({
    queryKey: ['notification-preferences', 'my'],
    queryFn: () => notificationPreferencesService.getMyPreferences(),
  });
}

export function useMyPhoneNumber() {
  return useQuery({
    queryKey: ['user', 'phone-number'],
    queryFn: () => notificationPreferencesService.getMyPhoneNumber(),
  });
}

export function useSmsOptInStatus() {
  return useQuery({
    queryKey: ['user', 'sms-opt-in'],
    queryFn: () => notificationPreferencesService.getSmsOptInStatus(),
  });
}

export function useSetSmsOptIn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (optedIn: boolean) =>
      notificationPreferencesService.setSmsOptIn(optedIn),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', 'sms-opt-in'] });
      queryClient.invalidateQueries({ queryKey: ['auth'] });
    },
  });
}

export function useUpdateMyPhoneNumber() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (phone_number: string | null) =>
      notificationPreferencesService.updateMyPhoneNumber(phone_number),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user', 'phone-number'] });
      queryClient.invalidateQueries({ queryKey: ['user', 'sms-opt-in'] });
      queryClient.invalidateQueries({ queryKey: ['auth'] });
    },
  });
}

export function useCreateNotificationPreference() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateUserNotificationPreferenceInput) =>
      notificationPreferencesService.createPreference(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences', 'my'] });
    },
  });
}

export function useUpdateNotificationPreference() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateUserNotificationPreferenceInput) =>
      notificationPreferencesService.updatePreference(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences', 'my'] });
    },
  });
}

export function useDeleteNotificationPreference() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      notificationPreferencesService.deletePreference(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences', 'my'] });
    },
  });
}

export function useUpsertNotificationPreference() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateUserNotificationPreferenceInput) =>
      notificationPreferencesService.upsertPreference(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences', 'my'] });
    },
  });
}

export function useSendTestNotification() {
  return useMutation({
    mutationFn: (channel: 'email' | 'sms') =>
      notificationPreferencesService.sendTestNotification(channel),
  });
}

// ============================================================================
// ADMIN PREFERENCES (for all users)
// ============================================================================

export function useAllUserNotificationPreferences() {
  return useQuery({
    queryKey: ['notification-preferences', 'all'],
    queryFn: () => notificationPreferencesService.getAllUserPreferences(),
  });
}

export function useUserNotificationPreferences(userId: string) {
  return useQuery({
    queryKey: ['notification-preferences', 'user', userId],
    queryFn: () => notificationPreferencesService.getUserPreferences(userId),
    enabled: !!userId,
  });
}

export function useUpdateUserPhoneNumber() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, phone_number }: { userId: string; phone_number: string | null }) =>
      notificationPreferencesService.updateUserPhoneNumber(userId, phone_number),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
}

export function useUpsertUserNotificationPreference() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, input }: { userId: string; input: CreateUserNotificationPreferenceInput }) =>
      notificationPreferencesService.upsertUserPreference(userId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
    },
  });
}

export function useUserSmsOptInStatus(userId: string) {
  return useQuery({
    queryKey: ['user', 'sms-opt-in', userId],
    queryFn: () => notificationPreferencesService.getUserSmsOptInStatus(userId),
    enabled: !!userId,
  });
}

export function useSetUserSmsOptIn() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, optedIn }: { userId: string; optedIn: boolean }) =>
      notificationPreferencesService.setUserSmsOptIn(userId, optedIn),
    onSuccess: (_, { userId }) => {
      queryClient.invalidateQueries({ queryKey: ['user', 'sms-opt-in', userId] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
  });
}

